import { useEffect, useMemo, useState } from "react";
import { Tree, Spin, Input, Empty, Tag, Typography, Space, Button } from "antd";
import { FiFolder, FiFile, FiCheck, FiX } from "react-icons/fi";
import axios from "axios";
import { API_BASE_URL } from "../api";

const { Search } = Input;

const TestsTreeView = ({ 
    onSelectTests, 
    multiple = false, 
    excludeTestIds = [], 
    selectedTests, 
    setSelectedTests,
    step = 1 
}) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const catRes = await axios.get(`${API_BASE_URL}/tests/categories`);
        if (!mounted) return;
        setCategories(Array.isArray(catRes.data) ? catRes.data.filter(t => !excludeTestIds.includes(t._id)) : []);
      } catch (err) {
        console.error("Error fetching test categories:", err);
        setCategories([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCategories();
    return () => (mounted = false);
  }, []);



  const isTestSelected = (test) => {
    const selected = selectedTests.some((t) => t.test?._id === test._id);
    // console.log(`isTestSelected for test ID ${test._id}:`, selected);
    return selected;
  }

  const addToSelectedTests = (test) => {
    if (isTestSelected(test)) {
      setSelectedTests((prev) => prev.filter((t) => t.test?._id !== test._id));
    } else {
      setSelectedTests((prev) => [...prev, { test, testPrice: test.price  }]);
    }
  };


  const treeData = useMemo(() => {
    const normalizedCategories = categories.map((c) => {
      const testsArr = Array.isArray(c.tests) ? c.tests : [];
      const catKey = c._id || c.id || `cat-${c.categoryId || c.categoryName}`;

      const title = (
        <Space size={8} align="center">
          <FiFolder style={{ color: '#1677ff' }} />
          <Typography.Text strong>{c.categoryName || c.categoryId || 'Unnamed Category'}</Typography.Text>
          <Tag color="blue" style={{ marginLeft: 8 }}>{testsArr.length}</Tag>
        </Space>
      );
      const children = testsArr.map((t) => {
        const testKey = t._id || `test-${t.testId}`;
        const childTitle = (
          <div className="w-96 flex justify-between items-center gap-5">
            <Space size={6} align="center" className="flex-1 overflow-x-auto">
              <FiFile style={{ color: '#444' }} />
              <span style={{ display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.title || t.testId}
              </span>
              { t.price !== undefined && (
                <Typography.Text type="secondary" className="text-nowrap">৳{t.price}</Typography.Text>
              )}
            </Space>
            <Button
              type="text"
              size="small"
              icon={ isTestSelected(t) ? <FiX />  : <FiCheck />}
              className={"flex justify-center items-center gap-2 bg-green-500/80 text-white hover:bg-green-500/20 text-green-700 hover:text-green-900 rounded-md" + ( isTestSelected(t) ? " bg-red-500/80 hover:bg-red-500/20 text-red-700 hover:text-red-900" : "")}
              onClick={(e) => {
                e.stopPropagation();
                console.log("Selected test:", t);
                addToSelectedTests(t);
              }}
            > 
              { isTestSelected(t) ? "UnSelect" : "Select"}
            </Button>
          </div>
        );
        return {
          key: testKey,
          title: childTitle,
          isLeaf: true,
          raw: t,
        };
      });

      return {
        key: catKey,
        title,
        children,
      };
    });

    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      return normalizedCategories
        .map((cat) => {
          const catMatches = (cat.title && String((cat.title.props && cat.title.props.children && cat.title.props.children.map ? cat.title.props.children.map(c => typeof c === 'string' ? c : (c && c.props && c.props.children) ) : '')).toLowerCase().includes(q)) || false;
          const matchedChildren = (cat.children || []).filter((child) => {
            const rawTitle = (child.raw && (child.raw.title || child.raw.testId)) || '';
            return String(rawTitle).toLowerCase().includes(q);
          });
          if (catMatches || matchedChildren.length > 0) {
            return { ...cat, children: matchedChildren };
          }
          return null;
        })
        .filter(Boolean);
    }
    return normalizedCategories;
  }, [categories, search, onSelectTests, selectedTests]);
  
  const handleListItemClick = (_selectedKeys, info) => {
    const evt = info && (info.event || info.nativeEvent || info.nativeEvent?.nativeEvent);
    if (evt && typeof evt.stopPropagation === 'function') {
      evt.stopPropagation();
    }
    const node = info && info.node;
    if (node && !node.isLeaf) {
      const key = node.key;
      setExpandedKeys((prev) => {
        if (prev && prev.includes(key)) {
          return prev.filter((k) => k !== key);
        }
        return [...(prev || []), key];
      });
    }
  };

  const onExpand = (keys) => {
    setExpandedKeys(keys);
  };

  return (
    <div className="p-0 backdrop-blur-md h-full w-full flex justify-between items-start">
      <div className="w-full p-2 bg-white border border-gray-300 rounded-lg shadow-lg h-[400px] flex flex-col overflow-y-auto">
          { step === 1 && <div style={{ marginBottom: 8 }}>
            <Search
              placeholder="Search categories or tests"
              allowClear
              onSearch={(val) => setSearch(val)}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              size="large"
            />
          </div> }

          <div className="mb-2 flex justify-between items-center">
            <Tag color="green">Selected Tests: {selectedTests.length}</Tag>

            { step === 2 &&
              <Button type="primary" onClick={() => {
                onSelectTests(selectedTests);
              }}>
                Add Selected Tests
              </Button>
            }
          </div>

          { loading ? (
            <div className="flex justify-center items-center py-8">
              <Spin />
            </div>
          ) : treeData.length === 0 ? (
              <Empty description={"No tests/categories"} />
          ) : (
            step === 1 ? 
              <Tree
                treeData={treeData}
                selectable
                multiple={multiple}
                onSelect={handleListItemClick}
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                defaultExpandAll={false}
              />
              :
              <div>
                { selectedTests.map(({ test, testPrice }) => (
                  <div key={test._id} className="p-2 mb-2 border border-gray-300 rounded-md flex justify-between items-center">
                    <div className="flex-1">
                      <Typography.Text>{test.title || test.testId}</Typography.Text>
                      { testPrice !== undefined && (
                        <Typography.Text type="secondary" className="ml-2">৳{test.price}</Typography.Text>
                      )}
                    </div>

                    { test.price !== testPrice && (
                      <p className="text-xs text-sub text-red-700 font-semibold mr-3">
                        - {test.price - testPrice}
                      </p>)
                      }

                    <Input type="number" min={0} step={0.01} defaultValue={testPrice} className="w-24 mr-4"
                      onChange={(e) => {
                        const newPrice = parseFloat(e.target.value);
                        setSelectedTests((prev) => prev.map((t) => {
                          if (t.test?._id === test._id) {
                            return { ...t, testPrice: isNaN(newPrice) ? 0 : newPrice };
                          }
                          return t;
                        }));
                      }}
                    />

                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={() => {
                        setSelectedTests((prev) => prev.filter((t) => t.test?._id !== test._id));
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )) }
              </div>
          )}
      </div>
    </div>
  );
};

export default TestsTreeView;